<template>
    <div class="profile-wrapper">
        <template v-if="$auth.ready()">
            <v-row justify="center">
                <v-col class='text-center' cols="2">
                    <router-link :to="{name: 'user.setting'}" class="linked">
                        <app-icon class="top-icon" icon="cog"/>
                    </router-link>
                </v-col>
                <v-col cols="8" class="pt-2 text-center profile-switch">
                    <v-menu v-model="menuOpen" close-on-content-click offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                class="elevation-0"
                                v-bind="attrs" v-on="on" >
                                <span class="text-lowercase">
                                    {{ user.name }}
                                </span>
                                <v-icon size="30">
                                    {{ menuOpen ? 'mdi-menu-down' : 'mdi-menu-right' }}
                                </v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item
                            v-for="(item, i) in accountOptions"
                            :key="i"
                            @click="handleSwitchAccount(item.id)"
                            >
                                <div class="d-flex align-center profile-account-lists">
                                    <user-image
                                        :src="item.img"
                                        :username="item.name"
                                    />
                                    <span class="ml-2">{{ item.name }}</span>
                                    <verified-icon
                                        v-if="item.is_verified" 
                                        :height="15" :width="15" class="ml-1" />
                                    <v-icon v-if="item.name === user.name" class="success--text">mdi-check-circle</v-icon>
                                </div>
                            </v-list-item>

                            <v-divider></v-divider>

                            <v-list-item
                                @click="handleAddAccount"
                                class="justify-center" >
                                <span>{{ $t('dialogs.switch_account.add_account_label') }}</span>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>
                <v-col class='text-center' cols="2">
                    <router-link :to="{name: 'user.own.messages'}" class="linked">
                        <v-badge
                            v-if="isActiveMessages"
                            :content="unreadMessagesStr"
                            color="#FE4811"
                            bottom
                            offset-x="14"
                            offset-y="16"
                        >
                            <app-icon icon="telegram" :active="isActiveMessages"/>
                        </v-badge>
                        <app-icon v-else icon="telegram" />
                    </router-link>
                </v-col>
            </v-row>
            <v-row justify="center">
                <profile-image
                    :img="userData.data.profileImage"
                    :username="user.name"
                    size="136"
                    :has-not-watched-stories="user.hasNotWatchedStories"
                    :is-exist-story="user.isExistStory"
                    class="mt-6"
                    :id="user.id"
                    :editable="user.isExistStory"
                    @click="handleClickProfile"
                />
            </v-row>
            <v-row justify="center">
                <div class="name d-flex justify-center align-center">
                    <span>
                        {{ user.name }}
                    </span>
                    <verified-icon
                        v-if="user.verified"
                        :height="24" :width="24" class="ml-2" />
                        <holiday-mood-icon v-if="userData.data.on_holiday_mood"/>
                </div>
            </v-row>
            <v-row justify="center">
                <v-col cols="4" class="ma-7 mt-4 text-end">
                    <router-link :to="{name: 'user.profile.followers', params: {id: user.id}}"
                                 class="link text-decoration-none">
                        <div class="text-center">
                        <span class="counters">
                           {{ user.followersCount }}
                        </span>
                            <span class="hint">{{ $t('pages.profile.main.followers') }}</span>
                        </div>
                    </router-link>
                </v-col>
                <v-col cols="3" class="ma-7 mt-4">
                    <router-link :to="{name: 'user.profile.following', params: {id: user.id}}"
                                 class="link text-decoration-none">
                        <div class="text-center">
                            <span class="counters"> {{ user.followingCount }} </span>
                            <span class="hint">{{ $t('pages.profile.main.following') }}</span>
                        </div>
                    </router-link>
                </v-col>
            </v-row>

            <v-row no-gutters justify="center">
                <v-col cols="12">
                    <router-link :to="{name: 'user.own.profile.info'}" class="link text-decoration-none">
                        <v-row justify="center" align="center">
                            <v-col cols="2">
                                <app-icon icon="write"/>
                            </v-col>
                            <v-col cols="8">
                                {{ $t('pages.profile.main.info') }}
                            </v-col>
                            <v-col cols="2" class="text-end">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-col>
                        </v-row>
                    </router-link>
                </v-col>
            </v-row>
            <v-row no-gutters justify="center">
                <v-col cols="12">
                    <router-link :to="{name: 'user.setting.creator'}" class="link text-decoration-none">
                        <v-row justify="center" align="center">
                            <v-col cols="2">
                                <app-icon icon="dollar"/>
                            </v-col>
                            <v-col cols="8">
                                {{ $t('pages.profile.main.creator') }}
                            </v-col>
                            <v-col cols="2" class="text-end">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-col>
                        </v-row>
                    </router-link>
                </v-col>
            </v-row>
            
            <v-row v-if="!isPWAInstalled" no-gutters justify="center">
                <v-col cols="12">
                    <v-row justify="center" align="center">
                        <v-col cols="2">
                            <v-icon size="25">mdi-download</v-icon>
                        </v-col>
                        <v-col cols="8" @click="openInstallDialog = true">
                            {{ $t('pages.profile.main.get_app') }}
                        </v-col>
                        <v-spacer/>
                        <app-transition>
                            <template v-if="openInstallDialog">
                                <instruction-popup v-if="!isEventCall" @yes="confirmInstallApp" @no="openInstallDialog = false" />
                                <yes-no-dialog v-else @yes="confirmInstallApp" @no="openInstallDialog = false">
                                    {{ $t('dialogs.are_you_sure') }}
                                </yes-no-dialog>
                            </template>
                        </app-transition>
                        <v-col cols="2">
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row no-gutters justify="center">
                <v-col cols="12">
                    <v-row justify="center" align="center">
                        <v-col cols="2">
                            <app-icon icon="share" @click="share"/>
                        </v-col>
                        <v-col cols="8">
                            <span ref="userlinkFull" :title="userlinkFull">{{ marketUserText }}</span>
                        </v-col>
                        <v-col cols="2" class="text-end" @click="copyLink">
                            <app-icon icon="copy"/>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <!-- Achievements -->
            <v-expansion-panels flat tile >
                <v-expansion-panel>
                    <v-expansion-panel-header class="pa-0 d-flex align-center" expand-icon="mdi-chevron-down">
                        <v-row class="align-center">
                            <v-col cols="2">
                                <hall-fames class="section-title" />
                            </v-col>
                            <v-col cols="8" class="pl-4">
                                {{ $t('pages.profile.main.achievements') }}
                            </v-col>
                            <v-col cols="2"></v-col>
                        </v-row>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <div v-if="achievementsLoading" class="achievements-scroll pl-10 pr-0 d-flex">
                            <v-card v-for="n in 3" :key="'skel-ach-'+n" class="pa-4 d-flex flex-column align-center justify-center text-center achievement-card ma-2 skeleton-card" outlined>
                                <v-skeleton-loader type="text" width="60" class="mb-2" />
                                <v-skeleton-loader type="avatar" width="50" height="50" class="mb-2" />
                                <v-skeleton-loader type="text" width="80" />
                            </v-card>
                        </div>

                        <v-slide-group v-else class="achievements-scroll pl-10 pr-0">
                            <template v-if="achievements.length > 0">
                                <v-slide-item
                                    v-for="(achievement, index) in achievements"
                                    :key="index"
                                >
                                    <v-card
                                        class="pa-4 d-flex flex-column align-center justify-center text-center achievement-card ma-2"
                                        outlined
                                    >
                                        <!-- Title -->
                                        <div class="achievements-title font-weight-bold mb-3">
                                        {{ achievement.category }}
                                        </div>
    
                                        <!-- Rank -->
                                        <div class="d-flex align-center justify-center mb-3" style="min-height: 50px;">
                                            <rank-first v-if="achievement.rank == 1" />
                                            <rank-second v-else-if="achievement.rank == 2" />
                                            <rank-third v-else-if="achievement.rank == 3" />
                                        </div>
    
                                        <!-- Date -->
                                        <div class="achievements-date grey--text text--lighten-1">
                                        {{ achievement.month }} {{ achievement.year }}
                                        </div>
                                    </v-card>
                                </v-slide-item>
                            </template>
                            <template v-else>
                                <div class="d-flex flex-column align-center justify-center">
                                    <div class="grey--text text--lighten-1">
                                        {{ $t('pages.profile.main.no_achievements') }}
                                    </div>
                                </div>
                            </template>
                        </v-slide-group>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>

            <!-- Top Tippers -->
            <div class="mt-2 mb-5">
                <v-expansion-panels flat tile>
                    <v-expansion-panel>
                        <!-- Header -->
                        <v-expansion-panel-header class="pa-0 d-flex align-center" expand-icon="mdi-chevron-down">
                            <v-row class="align-center">
                                <v-col cols="2">
                                    <tipper-icon class="section-title mr-3" />
                                </v-col>
                                <v-col cols="8" class="pl-4">
                                    <span>{{ $t('pages.profile.main.top_tippers') }}</span>
                                </v-col>
                                <v-col cols="2"></v-col>
                            </v-row>
                        </v-expansion-panel-header>

                        <v-expansion-panel-content>
                            <div v-if="tippersLoading" class="tipper-items">
                                <div v-for="n in 3" :key="'skel-tip-'+n" class="tipper-item" :class="{'first-place': n === 2, 'second-place': n === 1, 'third-place': n === 3}">
                                    <v-card class="pa-4 d-flex flex-column align-center justify-center tipper-card skeleton-card" outlined>
                                        <v-skeleton-loader type="text" width="30" class="mb-2" />
                                        <v-skeleton-loader type="avatar" width="46" height="46" class="mb-2" />
                                        <v-skeleton-loader type="text" width="60" />
                                    </v-card>
                                </div>
                            </div>
                            <div v-else class="tipper-items">
                                <template v-if="tippers.length > 0">
                                    <div
                                        v-for="(tipper, index) in tippers"
                                        :key="index"
                                        class="tipper-item"
                                        :class="{
                                            'first-place': index === 0,
                                            'second-place': index === 1,
                                            'third-place': index === 2
                                        }"
                                    >
                                        <v-card
                                            class="pa-4 d-flex flex-column align-center justify-center tipper-card"
                                            outlined
                                        >
                                            <div class="ranking-number">{{ index + 1 }}</div>
                                            <img v-if="tipper.avatar_url" :src="tipper.avatar_url" alt="tipper" class="avatar" />
                                            <img v-else :src="defaultAvatar" alt="tipper" class="avatar" />
                                            <div class="mt-2 tipper-username white--text">
                                                {{ tipper.name }}
                                            </div>
                                            <div class="tipper-value font-weight-bold">
                                                ${{ tipper.total_tipped }}
                                            </div>
                                        </v-card>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="d-flex flex-column align-center justify-center">
                                        <div class="grey--text text--lighten-1">
                                            {{ $t('pages.profile.main.no_tippers') }}
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div>

        </template>
        <bottom-bar :active="activeBottomBar" :model="false"></bottom-bar>
        <inform-dialog
            :text="$t('dialogs.not_contents')"
            v-if="this.isNotHaveContents"
            @close="handleCloseInformDialog"
        />

        <add-account-dialog
            v-if="showAddAccountDialog"
            @close="showAddAccountDialog = false"
            @login="loginToExistingAccount"
            @signup="createNewAccount"
        />
    </div>
</template>

<script>
import BottomBar from "@/components/app/navigation/BottomBar";
import {mapActions, mapMutations, mapState} from 'vuex'
import ProfileImage from "@/components/app/profile/ProfileImage";
import InformDialog from "@/components/app/dialogs/InformDialog";
import VerifiedIcon from "@/components/app/common/VerifiedIcon";
import InstructionPopup from "@/components/app/profile/InstructionPopup";
import YesNoDialog from "@/components/app/setting/YesNoDialog";
import AddAccountDialog from "@/components/app/dialogs/AddAccountDialog";
import UserImage from "@/components/app/notifications/ProfileImage";

import * as clipboardy from 'clipboardy'
import { DOMAIN_NAME, SERVER_PROTOCOL, LINK_ACCOUNT_KEY, PARENT_ACCOUNT_KEY } from "@/configs/constants";
import { setCookie } from "@/helpers/functions";
import HolidayMoodIcon from "@/components/app/icons/HolidayMoodIcon";
import hallFames from "@/assets/images/icons/hall-fames.svg?inline";
import TipperIcon from "@/assets/images/icons/tipper-icon.svg?inline";
import RankFirst from "@/assets/images/icons/rank-first.svg?inline";
import RankSecond from "@/assets/images/icons/rank-second.svg?inline";
import RankThird from "@/assets/images/icons/rank-third.svg?inline";
import defaultAvatar from "@/assets/images/home/swiper-9.jpg";

export default {
    name: "Profile.vue",
    data() {
        return {
            isShowEditStory: false,
            isNotHaveContents: false,
            openDialog: null,
            openInstallDialog: null,
            deferredPrompt: null,
            isPWAInstalled: true,
            isEventCall:false,
            showAddAccountDialog: false,
            defaultAvatar,
            achievements: [],
            achievementsLoading: true,
            tippers: [],
            tippersLoading: true,
            menuOpen: false,
        }
    },
    computed: {
        ...mapState({
            userData: state => state.user.user,
        }),
        user() {
            return this.$auth.user()
        },
        userlink() {
            return '/' + this.user.slug
        },
        userlinkFull() {
            return `${SERVER_PROTOCOL}://${DOMAIN_NAME}${this.userlink}?referral_code=${this.userData.referralCodes[0]?.value}`
        },
        marketUserText() {
            const userText = 'NOODZLY' + this.userlink
            return userText.toUpperCase()
        },
        unreadMessagesStr() {
            return this.unreadMessages > 9 ? '9+' : `${this.unreadMessages}`
        },
        unreadMessages() {
            return this.unread_messages;
        },
        isActiveMessages() {
            return this.unreadMessages > 0
        },
        accountOptions() {
            return [
                { id: this.user.id, name: this.user.name, img: this.user.profileImage, is_verified: this.user.verify_at },
                ...this.user.linked_accounts.map(account => ({
                    id: account.id,
                    name: account.name,
                    img: account.avatar_url,
                    is_verified: account.verify_at
                })),
            ];
        },
        ...mapState({
            unread_messages: state => state.user.user.data.unread_messages,
            activeBottomBar: state => state.site.activeBottomBar
        })
    },
    methods: {
        ...mapMutations(['showSnackBar']),
        ...mapActions(['getUserStories', 'getReferrals', 'switchAccount', 'checkAddAccountPermission', 'fetchAchievements', 'fetchTopTippersByUser']),
        share() {
            const shareData = {
                title: this.marketUserText,
                text: 'Check my profile',
                url: this.userlinkFull,
            }
            if (typeof navigator.share !== 'undefined')
                navigator.share(shareData)
                    .then(() => {
                        console.log('shared', shareData)
                    })
                    .catch(() => {
                        console.log('reject', shareData)
                    })
        },
        copyLink() {
            clipboardy.write(this.userlinkFull)
            this.showSnackBar(this.$t('dialogs.copied_clipboard'))
        },
        handleCloseEditStory() {
            this.isShowEditStory = false
        },
        handleCloseInformDialog() {
            this.isNotHaveContents = false
        },
        handleClickProfile() {
            if (!this.user.isExistStory) {
                this.isNotHaveContents = true
            }
        },
        
        closeInstallDialog() {
            this.openInstallDialog = null;
        },

        confirmInstallApp() {
            if (this.deferredPrompt) {
                this.deferredPrompt.prompt(); // Show the install prompt
                this.deferredPrompt.userChoice.then((choiceResult) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('PWA installation accepted');
                } else {
                    console.log('PWA installation dismissed');
                }
                this.deferredPrompt = null;
                });
            }
            this.closeInstallDialog();
        },
        checkPWAInstallation() {
            if (navigator.standalone) {
                this.isPWAInstalled = true;
            }
            else if (window.matchMedia('(display-mode: standalone)').matches) {
                this.isPWAInstalled = true;
            }
            else {
                console.log("PWA is not installed");
                this.isPWAInstalled = false;
            }
        },
        async handleAddAccount() {
            try {
                await this.checkAddAccountPermission();
                this.showAddAccountDialog = true;
            } catch (error) {
                this.showSnackBar(error)
            }
        },
        async handleSwitchAccount(userId){
            try {
                if (userId !== this.user.id) {
                    await this.setParentAccountKey();
                    await this.switchAccount(userId);
                    this.showSnackBar('Account switched successfully');
                }
            } catch (error) {
                this.showSnackBar(error)
            }
        },
        loginToExistingAccount() {
            setCookie(LINK_ACCOUNT_KEY, this.user.name, 1);
            this.setParentAccountKey();
            this.$router.push({ name: 'login' });
        },
        createNewAccount() {
            setCookie(LINK_ACCOUNT_KEY, this.user.name, 1);
            this.setParentAccountKey();
            this.$router.push({ name: 'sign-up' });
        },
        setParentAccountKey(){
            setCookie(PARENT_ACCOUNT_KEY, this.user.slug, 1 * 24 * 7);
        },
    },
    components: {
        ProfileImage,
        BottomBar,
        InformDialog,
        VerifiedIcon,
        InstructionPopup,
        YesNoDialog,
        AddAccountDialog,
        UserImage,
        HolidayMoodIcon,
        hallFames,
        TipperIcon,
        RankFirst, RankSecond, RankThird
    },
    async mounted() {
        this.getReferrals();
        this.fetchAchievements(this.user.id).then((res) => {
            this.achievements = res;
        }).finally(() => {
            this.achievementsLoading = false;
        });
        this.fetchTopTippersByUser().then((res) => {
            this.tippers = res.tippers;
        }).finally(() => {
            this.tippersLoading = false;
        });
    },
    created() {
        const userId = this.$auth.user().id
        this.$echo.private(`user.${userId}`).listen('.story.processed', this.getUserStories)
        this.checkPWAInstallation()
        window.addEventListener("beforeinstallprompt", (event) => {
            event.preventDefault();
            this.deferredPrompt = event;
            console.log("beforeinstallprompt fired in created");
            this.isEventCall = true
        });

        this.$mixpanel.identifyUser(this.userData.data.email);
        this.$mixpanel.pageViewed('Account');
    }
}
</script>

<style scoped lang="scss">
@import '@/sass/modules/_variables';

.Profile {
    &-wrapper {
        min-height: 100%;
    }
}

.top-icon {
    font-size: 28px;
}

.profile-image {
    height: 136px;
    width: 136px;
    border-radius: 50%;
    background-repeat: no-repeat;
    background-size: cover;
    background-color: whitesmoke;
    border: 6px solid black;
    object-fit: cover;
}

.image-container {
    height: 148px;
    width: 148px;
    border-radius: 50%;
    padding-left: 6px;
    padding-top: 6px;
}

.story-exist {
    background: $accent-background;
}

.name {
    font-size: 1.5em;
}

.counters {
    font-size: 1.5em;
}

.hint {
    display: block;
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.6);
}

.linked {
    text-decoration: none;
}

.profile-account-lists {
    i {
        position: absolute;
        right: 10px;
    }
}

.profile-switch { 
   .v-btn  {
        min-width: 200px;
   }
}

.profile-wrapper { padding: 24px 24px 100px; }
.profile-wrapper h3 { font-weight: 700; font-size: 24px; line-height: 160%; }
.profile-wrapper .achievements-scroll { display: flex; gap: 7px; margin: 0px -8px; }
.profile-wrapper .achievements-scroll .achievement-card { width: 33.33%; background-color: #171717;min-width: 124px; border-radius: 10px; }
.profile-wrapper .achievements-scroll .achievement-card svg { margin: 10px 0; }
.profile-wrapper .achievements-title { font-weight: 500 !important; font-size: 14px; line-height: 160%; color: #FFFFFF; }
.profile-wrapper .achievements-date { font-weight: 500; font-size: 12px; line-height: 160%; color: rgba(255, 255, 255, 0.5); }

h3.section-title { display: flex; align-items: center; justify-items: flex-start; gap: 16px; font-weight: 700; font-size: 25px; line-height: 160%; }

.tipper-items { display: flex; align-items: flex-end; gap: 7px; }
.tipper-items .tipper-item { width: 33.33%; }
.tipper-items .tipper-item .tipper-card { background: rgba(23, 23, 23, 1); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 8px; padding: 8px 15px; }
.tipper-items .tipper-item.first-place { height: 190px; order: 1; }
.tipper-items .tipper-item.second-place { height: 162px;  order: 0; }
.tipper-items .tipper-item.third-place { height: 135px; order: 2; }
.tipper-items .tipper-item.first-place .tipper-card { height: 190px; order: 1; background: rgba(255, 120, 5, 1); }
.tipper-items .tipper-item.first-place .tipper-card .ranking-number { color: #000000; font-weight: 700; font-size: 36px; line-height: 160%; }
.tipper-items .tipper-item.second-place .tipper-card { height: 162px;  order: 0; }
.tipper-items .tipper-item.third-place .tipper-card { height: 135px; order: 2; }

.tipper-items .tipper-item.second-place .tipper-card .ranking-number, .tipper-items .tipper-item.third-place .tipper-card .ranking-number { color: rgba(255, 120, 5, 1); font-weight: 700; font-size: 22px; line-height: 160%; }

.tipper-items .tipper-item .avatar { height: 46px !important; width: 46px !important; border-radius: 100%; border: 2px solid rgba(255, 120, 5, 1); }
.tipper-items .tipper-item.first-place .avatar { height: 70px !important; width: 70px !important; border: 2px solid #fff;}
//  outline: dashed 2px #FFFFFF; outline-offset: 3px; 
.tipper-items .tipper-item.first-place .tipper-card .tipper-username { color: rgba(23, 23, 23, 1) !important; font-size: 16px; font-weight: 500; }
.tipper-items .tipper-item.first-place .tipper-card .tipper-value { font-size: 16px; font-weight: 500 !important; color: rgba(23, 23, 23, 1) !important;  }
.tipper-items .tipper-item .tipper-card .tipper-username { font-weight: 500; font-size: 14px; line-height: 160%; color: rgba(255, 255, 255, 1); }
.tipper-items .tipper-item .tipper-card .tipper-value { color: rgba(255, 255, 255, 0.5); font-weight: 500 !important; font-size: 12px; line-height: 160%; }
.achievements-scroll {
  overflow: visible !important;
  padding-right: 16px; /* adjust as needed */
}
.v-slide-group__wrapper {
  overflow: visible !important;   /* let content show fully */
  padding-right: 12px;            /* space for the scrollbar */
}

.v-slide-group__content {
  margin-right: 0 !important;     /* remove hidden cutoff */
}
</style>
